// @story HIR-CN5
// WhatsAppAssistant — Hiring's own WhatsApp bot for candidates (server:
// agents/whatsappAssistant.js; GET / PUT /api/school-settings/whatsapp-
// assistant). It answers a candidate who writes to the school's WhatsApp
// number as a polite HR person, from their own applications only, and gives
// the HR email for anything it cannot answer. Off by default. A recruitment
// administrator sets the HR email, switches it on or off with a reason, and
// can try a question first: the answer shows here and is never sent.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Input, StatusPill, Switch } from '../../ds';
import { Loading, Note } from '../../components/page';

const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };
const when = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function isAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.role === 'admin' || (u.permissions || []).includes('recruitment:admin');
  } catch { return false; }
}

export default function WhatsAppAssistant() {
  const admin = isAdmin();
  const [st, setSt] = useState({ phase: 'loading' });
  const [asking, setAsking] = useState(null); // { on, hrEmail }
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState('');
  const [tried, setTried] = useState(null);

  const load = useCallback(() => {
    api.get('/school-settings/whatsapp-assistant')
      .then((r) => setSt({ phase: 'ready', ...r.data.data }))
      .catch(() => setSt({ phase: 'error' }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = () => {
    if (reason.trim().length < 12) { setMsg({ tone: 'warning', text: 'Give a reason of at least 12 characters. It is kept with the change.' }); return; }
    setBusy(true); setMsg(null);
    api.put('/school-settings/whatsapp-assistant', { on: asking.on, hrEmail: asking.hrEmail.trim(), reason: reason.trim() })
      .then((r) => {
        const to = r.data.data.to;
        setMsg({ tone: 'success', text: `WhatsApp assistant: ${to.on ? 'on' : 'off'}${to.hrEmail ? `, HR email ${to.hrEmail}` : ''}.` });
        setAsking(null); setReason(''); load();
      })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not change it. Try again.' }))
      .finally(() => setBusy(false));
  };

  const tryIt = () => {
    if (!question.trim()) return;
    setBusy(true); setTried(null);
    api.post('/school-settings/whatsapp-assistant/try', { question: question.trim() })
      .then((r) => setTried(r.data.data))
      .catch((e) => setTried({ error: e?.response?.data?.message || 'Could not try it just now.' }))
      .finally(() => setBusy(false));
  };

  if (st.phase === 'loading') return <Loading label="Reading the WhatsApp assistant" />;
  if (st.phase === 'error') return <Note tone="warning">The WhatsApp assistant could not be read just now. Reload the page to try again.</Note>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        When a candidate writes to the school&apos;s WhatsApp number, the assistant replies as a polite HR person, from their own application only.
        It never shares scores, chances or personal details. For anything else it says it does not have the information and gives the HR email.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 4, minWidth: 260, flex: 1 }}>
          <strong style={{ fontSize: 'var(--fs-body-sm)' }}>Answers candidates on WhatsApp</strong>
          <span style={muted}>HR email given to candidates: {st.hrEmail || 'not set yet'}</span>
          {st.changedAt && <span style={muted}>Changed by {st.changedBy} on {when(st.changedAt)}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusPill tone={st.on ? 'success' : undefined}>{st.on ? 'On' : 'Off'}</StatusPill>
          {admin && (
            <Switch checked={st.on} label={st.on ? 'On' : 'Off'}
              onChange={() => { setAsking({ on: !st.on, hrEmail: st.hrEmail || '' }); setReason(''); setMsg(null); }} />
          )}
          {admin && !asking && (
            <Button variant="ghost" onClick={() => { setAsking({ on: st.on, hrEmail: st.hrEmail || '' }); setReason(''); setMsg(null); }}>Change HR email</Button>
          )}
        </div>
      </div>
      {asking && (
        <div style={{ display: 'grid', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
          <strong>{asking.on ? (st.on ? 'Change the HR email?' : 'Switch the WhatsApp assistant on?') : 'Switch the WhatsApp assistant off?'}</strong>
          <span style={muted}>
            {asking.on
              ? 'From the next candidate message, the assistant replies on WhatsApp. Its replies go out only while candidate WhatsApp is on in Messages.'
              : 'From the next candidate message, nobody replies automatically. Messages still reach the hiring team.'}
          </span>
          <Input label="HR email given to candidates" value={asking.hrEmail} onChange={(e) => setAsking({ ...asking, hrEmail: e.target.value })} />
          <Input label="Why, and on whose word" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={save} disabled={busy}>Save</Button>
            <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
          </div>
        </div>
      )}
      {admin && (
        <div style={{ display: 'grid', gap: 8 }}>
          <Input label="Try a question, as a candidate would (nothing is sent)" value={question} onChange={(e) => setQuestion(e.target.value)} />
          <div><Button variant="secondary" onClick={tryIt} disabled={busy || !question.trim()}>Try it</Button></div>
          {tried && (tried.error
            ? <Note tone="warning">{tried.error}</Note>
            : <Note tone="info">{tried.action === 'reply' ? `It would reply: "${tried.text}"` : 'It would not reply (its sign-in is not set up yet).'}</Note>)}
        </div>
      )}
      {!admin && <Note tone="info">Only a recruitment administrator can switch the WhatsApp assistant on or off.</Note>}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}
